/**
 * Voyado Elevate: shared core.
 *
 * Everything more than one Elevate surface needs lives here: SDK readiness,
 * escaping, price formatting, click notifications and strings.
 *
 * Why this is a single file: a previous implementation of this integration ended
 * up with the product card duplicated four times, escaping helpers duplicated
 * across three to four files, and three different strategies for waiting on
 * `window.elevate` spread over eight files. We consolidate from day one.
 *
 * Exposed as `window.CGElevate` rather than an ES module, because ES modules
 * cannot statically import Shopify's hashed asset URLs. Same pattern as the
 * theme's existing `window.theme` and `formatMoney`.
 *
 * See docs/voyado-elevate-implementering.md section 12.
 */

(function () {
  'use strict';

  const READY_POLL_MS = 100;
  const READY_MAX_TRIES = 50;

  let readyPromise = null;

  function isUsable(candidate) {
    return !!(candidate && candidate.query && candidate.query.c);
  }

  /**
   * `hasAnalyticsConsent()` below is for OUR OWN theme code (elevate-session.js:
   * discoveryKey sync, eClub identification, cart attribute stamping) to gate
   * side effects that talk to Engage or write order-attribution data - not
   * for gating Elevate's own query/notify requests.
   *
   * The Elevate app embed's own bundled script (elevate-init.js/consent.js,
   * loaded from Shopify's extension CDN, not by this theme) already gates
   * those itself: its `session` config is a function that checks
   * `Shopify.customerPrivacy.analyticsProcessingAllowed()` and returns the
   * bare string `"ANONYMOUS"` when consent is withheld, instead of real
   * keys. Read the SDK's own source to confirm this reaches both surfaces
   * correctly (2026-09-22): `createBaseUrl()` - used by BOTH
   * `queries/${endpoint}` and `notifications/${endpoint}` - resolves that
   * one `session` function and writes whatever it returns straight into the
   * request's query parameters, so query and notify calls were never at
   * risk of using different, inconsistently-gated sessions in the first
   * place; there is exactly one `session` config, shared from the moment
   * `window.elevate` is constructed.
   *
   * An earlier version of this file duplicated that gating on top of the
   * app's own (wrapping `config.session` on `.c`/`.__config` with a second,
   * theme-side consent check and an "omit both keys" fallback for
   * query.*, plus a separate random-key fallback for notify.click/
   * addToCart). That duplicate turned out to be the source of a real bug,
   * not a safety net: `elevate.query.c` and `elevate.notify.c` are the SAME
   * object in the bundled SDK, so only the first of those two fallbacks
   * ever actually took effect for both surfaces - query's ran first and
   * silently won, so notify.click/addToCart kept using query's fallback and
   * got rejected with "Missing required parameter: 'sessionKey'" regardless
   * of what notify's own fallback said. Removed entirely 2026-09-22 in favor
   * of just trusting the app's already-correct, already-shared mechanism -
   * confirmed live that a bare `"ANONYMOUS"` string from `session()`
   * produces the exact same accepted request as the object-shaped fallback
   * this file used to substitute in.
   *
   * See docs/voyado-elevate-implementering.md section 4 and
   * https://product-discovery.developer.voyado.com/docs/integration/site-integration/session-management.md.
   */
  let customerPrivacyReady = null;

  function loadCustomerPrivacy() {
    if (customerPrivacyReady) return customerPrivacyReady;
    customerPrivacyReady = new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.currentVisitorConsent === 'function' && cp.currentVisitorConsent() !== undefined) {
          finish();
          return;
        }
        const loadFeatures = window.Shopify && window.Shopify.loadFeatures;
        if (typeof loadFeatures !== 'function') {
          finish();
          return;
        }
        loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finish);
        // The callback is not always invoked in async mode.
        setTimeout(finish, 1500);
      } catch (e) {
        finish();
      }
    });
    return customerPrivacyReady;
  }

  function hasAnalyticsConsent() {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    const consent = cp && typeof cp.currentVisitorConsent === 'function' && cp.currentVisitorConsent();
    return !!(consent && (consent.analytics === 'yes' || consent.analytics === true));
  }

  /**
   * The Elevate app loads through an app embed and may be assigned after this
   * script runs. A property trap catches the assignment synchronously as it
   * happens. Plain polling leaves a window where the SDK exists but nothing has
   * reacted yet, and in the reference implementation that is the most fragile
   * part of the integration.
   *
   * Resolves with the SDK, or with null if it never appears. Null is a normal
   * state: the app may be uninstalled, or blocked by consent management.
   * Consumers must degrade silently.
   *
   * Kicks off loadCustomerPrivacy() here too, not just where
   * hasAnalyticsConsent() is actually read (elevate-session.js): that read is
   * synchronous, so the Customer Privacy API has to already be loaded by the
   * time it happens, and every caller today only runs after this promise
   * resolves anyway.
   */
  function whenReady() {
    if (readyPromise) return readyPromise;

    readyPromise = new Promise((resolve) => {
      if (isUsable(window.elevate)) {
        loadCustomerPrivacy();
        resolve(window.elevate);
        return;
      }

      let settled = false;
      const done = (value) => {
        if (settled) return;
        settled = true;
        if (isUsable(value)) loadCustomerPrivacy();
        resolve(value);
      };

      try {
        let stored = window.elevate;
        Object.defineProperty(window, 'elevate', {
          configurable: true,
          get() {
            return stored;
          },
          set(value) {
            stored = value;
            if (isUsable(value)) done(value);
          }
        });
      } catch (e) {
        // Property cannot be redefined. The polling below covers us.
      }

      let tries = 0;
      (function poll() {
        if (settled) return;
        if (isUsable(window.elevate)) {
          done(window.elevate);
          return;
        }
        tries += 1;
        if (tries >= READY_MAX_TRIES) {
          done(null);
          return;
        }
        setTimeout(poll, READY_POLL_MS);
      })();
    });

    return readyPromise;
  }

  /**
   * Escaping. Everything from Elevate is merchandiser-controlled text that ends
   * up in innerHTML, so this has to be complete.
   *
   * The reference implementation's attribute escape only covered quotes and let
   * `&` and `<` through. A hex value containing a semicolon could then inject
   * CSS properties through `style="background-color:..."`.
   */
  const ESCAPE_MAP = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };

  function esc(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[&<>"']/g, (c) => ESCAPE_MAP[c]);
  }

  /**
   * Only http, https and relative URLs pass. Blocks `javascript:` URLs coming
   * from the feed.
   */
  function safeUrl(value) {
    if (!value) return '';
    const raw = String(value).trim();
    if (raw.startsWith('//')) return 'https:' + raw;
    // Internal Elevate links are path-relative, e.g. `/products/handle`.
    if (raw.startsWith('/')) return raw;

    // Absolute URLs are parsed without a base, so this function does not depend
    // on window.location being present.
    try {
      const url = new URL(raw);
      if (url.protocol === 'http:' || url.protocol === 'https:') return url.href;
      return '';
    } catch (e) {
      // Not absolute. Fall through to base-relative parsing below.
    }

    try {
      const base = (window.location && window.location.origin) || 'https://localhost';
      const url = new URL(raw, base);
      if (url.protocol === 'http:' || url.protocol === 'https:') return url.pathname + url.search;
    } catch (e) {
      return '';
    }
    return '';
  }

  /**
   * Elevate reports prices in major units, while the theme's `formatMoney`
   * expects minor units. We deliberately reuse it instead of
   * `Intl.NumberFormat`, so that Elevate-rendered prices are character for
   * character identical to the rest of the site. The format comes from
   * `shop.money_format` through `window.theme.moneyFormat`.
   */
  function money(amount) {
    const value = Number(amount);
    if (!isFinite(value)) return '';
    if (typeof formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return formatMoney(Math.round(value * 100));
    }
    return value.toFixed(2).replace('.', ',') + ' kr';
  }

  /**
   * Elevate returns `sellingPrice` and `listPrice` as `{min, max}`. Product
   * groups spanning a price range, for example CG's 4-packs and 6-packs, must
   * render with a "from" prefix. The reference implementation ignored `max`
   * entirely and showed a single price, which is actively misleading for this
   * assortment.
   */
  function priceRange(price) {
    if (!price) return '';
    const min = price.min;
    const max = price.max;
    if (min === null || min === undefined) return '';
    if (max !== null && max !== undefined && Number(max) > Number(min)) {
      return t('price_from', 'From') + ' ' + money(min);
    }
    return money(min);
  }

  /**
   * Strings come from the locale files through snippets/elevate-config.liquid,
   * never hardcoded here. The reference implementation had Norwegian copy and
   * " kr" scattered across four JS files, which makes the theme untranslatable.
   */
  function t(key, fallback) {
    const strings = window.CGElevateStrings || {};
    return strings[key] || fallback || '';
  }

  const LAST_TICKET_KEY = 'cg_elevate_last_ticket';

  /**
   * Click notification. Elevate learns nothing without it, and without a ticket
   * it cannot tell which object the visitor interacted with.
   *
   * The SDK sends with keepalive, so the call survives the navigation that
   * follows immediately after. We therefore do not need to delay navigation.
   *
   * Also remembers the ticket, best-effort, so a later add-to-cart on a
   * product page reached by this exact click can still be attributed - see
   * notifyAddToCart()/lastClickedTicket() below and assets/elevate-session.js.
   * Last-touch only: clicking a second product before adding the first to
   * cart overwrites it. Storage failures (private browsing, quota) are
   * swallowed the same as a failed notify call.
   */
  function notifyClick(ticket) {
    if (!ticket) return;
    try {
      sessionStorage.setItem(LAST_TICKET_KEY, ticket);
    } catch (e) {
      // Ignore, see above.
    }
    const elevate = window.elevate;
    if (!elevate || !elevate.notify || typeof elevate.notify.click !== 'function') return;
    try {
      elevate.notify.click(ticket);
    } catch (e) {
      // Tracking must never break navigation.
    }
  }

  /**
   * Add-to-cart notification. Required per Voyado's own notification
   * checklist (docs/voyado-elevate-implementering.md section 7) - previously
   * missing entirely. `ticket` is whichever one assets/elevate-session.js
   * decided applies to the submitted product; a product added without ever
   * being clicked through Elevate (a bookmark, a direct PDP visit) has none,
   * and callers are expected to skip the call rather than send a wrong one.
   */
  function notifyAddToCart(ticket) {
    if (!ticket) return;
    const elevate = window.elevate;
    if (!elevate || !elevate.notify || typeof elevate.notify.addToCart !== 'function') return;
    try {
      elevate.notify.addToCart(ticket);
    } catch (e) {
      // Tracking must never break the cart.
    }
  }

  /**
   * Favorite notifications, required for Elevate's FAVORITES algorithm to
   * have any data at all - see docs/voyado-elevate-implementering.md
   * section 7.
   *
   * Unlike click/add-to-cart these take a productKey, not a ticket: a
   * favorite is an opinion about a product, not about the particular
   * rendering the visitor interacted with, so there is nothing to attribute
   * to a list. Elevate's productKey is the Shopify product id, which the
   * wishlist app already carries on its own markup - confirmed against a
   * live response (assets/elevate-session.js's favorites bridge).
   */
  function notifyAddFavorite(productKey) {
    notifyFavorite('addFavorite', productKey);
  }

  function notifyRemoveFavorite(productKey) {
    notifyFavorite('removeFavorite', productKey);
  }

  function notifyFavorite(method, productKey) {
    if (!productKey) return;
    const elevate = window.elevate;
    if (!elevate || !elevate.notify || typeof elevate.notify[method] !== 'function') return;
    try {
      elevate.notify[method](String(productKey));
    } catch (e) {
      // Tracking must never break the wishlist button.
    }
  }

  /**
   * The ticket from the most recent notifyClick() call, or '' if there has
   * not been one this session - see the comment there.
   */
  function lastClickedTicket() {
    try {
      return sessionStorage.getItem(LAST_TICKET_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Custom attributes to request, shared by every surface so they cannot drift
   * apart.
   *
   * `presentCustom` is a pipe-separated list of attribute names, not a boolean.
   * Passing `true` requests an attribute literally named "true" and returns
   * nothing, which is what made an earlier measurement conclude that the
   * catalogue held no custom data at all.
   *
   * `tags` and variant options are synced automatically by the Shopify app.
   * `hide_member_discount` is a product metafield that has to be mapped in the
   * app first; requesting an attribute that does not exist is silently
   * ignored, so it is safe to ask for it before that happens.
   *
   * Attribute ids are lowercased in the response, and å/ø/æ are stripped, since
   * the catalogue requires XML-compliant names.
   *
   * See docs/voyado-elevate-implementering.md section 3.
   */
  const PRESENT_CUSTOM = 'tags|hide_member_discount';

  /** Reads one custom attribute as a plain array of strings. */
  function customValues(source, name) {
    const group = (source && source.custom && source.custom[name]) || [];
    if (!Array.isArray(group)) return [];
    return group.map((item) => (item && item.id) || '').filter(Boolean);
  }

  /**
   * The member badge rule.
   *
   * Driven by a tag, because a tag is the only member signal that reaches the
   * catalogue. `Member Price` is a variant metafield, and the Shopify app syncs
   * variant *options* automatically but no variant metafields, while its mapping
   * UI covers product metafields only. Tags arrive automatically.
   *
   * Matches the live theme's own rule on the product card
   * (snippets/product-card.liquid), which checks this exact tag - not the
   * singular `Kategori: Faste medlemstilbud` a stray comment there once
   * suggested, confirmed against the Storefront API to match zero real
   * products. This plural form is the one the catalogue actually carries.
   *
   * This deliberately does not depend on member price. The two are separate in
   * the theme as well, and a product can carry one without the other.
   *
   * Lives here rather than in elevate-card.js because the autocomplete panel
   * needs the same rule and the card script only loads on the search page.
   *
   * See docs/voyado-elevate-implementering.md section 2.
   */
  const MEMBER_TAG = 'Kategorier: Faste medlemstilbud';

  function isMember(product) {
    // Matches snippets/product-card.liquid's own suppression field
    // (custom.hide_member_discount) - not hide_member_badge, which does not
    // exist as a metafield in this store.
    if (customValues(product, 'hide_member_discount').some((value) => /^true$/i.test(value))) return false;
    return customValues(product, 'tags').indexOf(MEMBER_TAG) !== -1;
  }

  /**
   * Title with the brand prefix removed.
   *
   * Every Elevate surface shows the brand on its own line, so a title that
   * repeats it reads as "Lyngby Glas / Lyngby Glas Moscow Mule Kopp 40cl".
   * Elevate returns the full Shopify title, brand included, and the theme's own
   * card solves it the same way, see snippets/product-card.liquid:397-411.
   *
   * Lives here because the search modal and the grid card both need it, and they
   * load on different pages.
   */
  function titleWithoutBrand(product) {
    const title = String((product && product.title) || '');
    const brand = String((product && product.brand) || '');
    if (!brand) return title;
    if (title.slice(0, brand.length).toLowerCase() !== brand.toLowerCase()) return title;
    // A product whose entire title is the brand keeps it, rather than leaving an
    // empty line where the name should be.
    return title.slice(brand.length).trim() || title;
  }

  /**
   * The brand as the title spells it, rather than as `product.brand` does.
   *
   * Elevate's `brand` field comes through shouting - "HADELAND GLASSVERK" -
   * while the same brand opens the title in ordinary case, and Shopify's own
   * vendor field matches the title. A card that reads the field directly
   * therefore changes case the moment it is replaced by a native
   * product-card.liquid, which is what the search panel does.
   *
   * Taken from the title rather than case-converted, because no rule survives
   * real brand names: title-casing would turn CG into Cg, and lowercasing
   * would ruin an acronym. The correctly cased string already exists in the
   * data, so this reads it off instead of inventing one. Falls back to the
   * field when the title does not start with the brand.
   */
  function brandAsTitled(product) {
    const title = String((product && product.title) || '');
    const brand = String((product && product.brand) || '');
    if (!brand) return '';
    if (title.slice(0, brand.length).toLowerCase() !== brand.toLowerCase()) return brand;
    return title.slice(0, brand.length);
  }

  /**
   * Elevate's Page ID convention always has a leading slash (`/frontpage`,
   * `/30-cg-medlem`). A merchandiser typing an id into a theme text setting
   * easily leaves it off, and Experience shows the page as merely "unused"
   * rather than raising an error, so the mistake otherwise goes unnoticed
   * while the request silently misses the configured page.
   */
  function normalizePageReference(value) {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    return trimmed.charAt(0) === '/' ? trimmed : '/' + trimmed;
  }

  window.CGElevate = {
    whenReady: whenReady,
    hasAnalyticsConsent: hasAnalyticsConsent,
    loadCustomerPrivacy: loadCustomerPrivacy,
    esc: esc,
    safeUrl: safeUrl,
    money: money,
    priceRange: priceRange,
    t: t,
    notifyClick: notifyClick,
    notifyAddToCart: notifyAddToCart,
    notifyAddFavorite: notifyAddFavorite,
    notifyRemoveFavorite: notifyRemoveFavorite,
    lastClickedTicket: lastClickedTicket,
    PRESENT_CUSTOM: PRESENT_CUSTOM,
    customValues: customValues,
    isMember: isMember,
    titleWithoutBrand: titleWithoutBrand,
    brandAsTitled: brandAsTitled,
    normalizePageReference: normalizePageReference
  };
})();
